import { useMemo } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { ContractStatusEnum, type Contract } from "@/generated/types";
import Currency from "../Currency";
import DateDisplay from "../DateDisplay";
import InstallmentRow from "./InstallmentRow";

interface ContractCardProps {
  contract: Contract;
}

const STATUS_VARIANT: Record<
  ContractStatusEnum,
  "default" | "success" | "destructive"
> = {
  [ContractStatusEnum.Pending]: "default",
  [ContractStatusEnum.Paid]: "success",
  [ContractStatusEnum.Overdue]: "destructive",
  [ContractStatusEnum.Complete]: "success",
};

const STATUS_LABEL: Record<ContractStatusEnum, string> = {
  [ContractStatusEnum.Pending]: "Pendente",
  [ContractStatusEnum.Paid]: "Pago",
  [ContractStatusEnum.Overdue]: "Atrasado",
  [ContractStatusEnum.Complete]: "Concluído",
};

export default function ContractCard({ contract }: ContractCardProps) {
  const sortedInstallments = useMemo(
    () => [...contract.installments].sort((a, b) => a.number - b.number),
    [contract.installments]
  );

  return (
    <Collapsible asChild>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full text-left cursor-pointer">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="text-base font-light text-primary-950 dark:text-white truncate">
                      {contract.name}
                    </h4>
                    <Badge variant={STATUS_VARIANT[contract.status]}>
                      {STATUS_LABEL[contract.status]}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-primary-600 dark:text-primary-400">
                    <span>
                      {contract.installments.length} parcela
                      {contract.installments.length !== 1 ? "s" : ""}
                    </span>
                    <DateDisplay date={contract.startDate} />
                    <Currency value={contract.totalValue} />
                  </div>
                </div>
              </div>
            </CardContent>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-primary-100 dark:border-primary-800">
            <CardContent className="p-6 pt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Parcela</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Pago em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedInstallments.map((installment) => (
                    <InstallmentRow
                      key={installment.id}
                      installment={installment}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
